import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import {
    Users,
    Plus,
    Search,
    Edit,
    Trash2,
    Shield,
    User,
    Mail,
    Calendar,
    Eye,
    UserCheck,
    UserX,
    Upload,
    X as XIcon
} from 'lucide-react';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Loading from '../components/common/Loading';
import { usersService } from '../services/users';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const getRoleColor = (role) => {
    switch (role) {
        case 'admin': return 'bg-blue-100 text-blue-800';
        case 'master': return 'bg-yellow-100 text-yellow-800';
        case 'lector': return 'bg-green-100 text-green-800';
        case 'xd': return 'bg-purple-100 text-purple-800';
        case 'sst': return 'bg-orange-100 text-orange-800';
        case 'validador': return 'bg-teal-100 text-teal-800';
        case 'limpieza': return 'bg-emerald-100 text-emerald-800';
        case 'rrhh': return 'bg-pink-100 text-pink-800';
        case 'user': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
    }
};

const getRoleText = (role) => {
    switch (role) {
        case 'admin': return 'Administrador';
        case 'master': return 'Master';
        case 'lector': return 'Lector';
        case 'xd': return 'XD';
        case 'sst': return 'SST';
        case 'validador': return 'Validador';
        case 'limpieza': return 'Limpieza';
        case 'rrhh': return 'RRHH';
        case 'user': return 'Usuario';
        default: return 'Usuario';
    }
};

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('view');
    const [selectedUser, setSelectedUser] = useState(null);
    const { isAdmin } = useAuth();

    // Cargar usuarios al montar el componente
    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await usersService.getAll();
            setUsers(data);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setModalType('edit');
        setSelectedUser(user);
        setShowModal(true);
    };

    const handleView = (user) => {
        setModalType('view');
        setSelectedUser(user);
        setShowModal(true);
    };

    const handleDelete = async (user) => {
        if (window.confirm(`¿Estás seguro de eliminar al usuario ${user.full_name}?`)) {
            try {
                await usersService.delete(user.id);
                toast.success('Usuario eliminado correctamente');
                loadUsers();
            } catch (error) {
                console.error('Error al eliminar usuario:', error);
                toast.error('Error al eliminar usuario');
            }
        }
    };

    const handleUpdateUser = async ({ firmaFile, ...userData }) => {
        try {
            await usersService.update(selectedUser.id, userData);
            if (firmaFile) {
                await usersService.uploadFirma(selectedUser.id, firmaFile);
            }
            toast.success('Usuario actualizado correctamente');
            setShowModal(false);
            loadUsers();
        } catch (error) {
            console.error('Error al actualizar usuario:', error);
            toast.error('Error al actualizar usuario');
        }
    };

    const filteredUsers = users.filter(user =>
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );


    if (!isAdmin()) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Acceso Denegado</h1>
                        <p className="text-gray-600">No tienes permisos para acceder a esta página.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (loading) {
        return (
            <Layout>
                <Loading message="Cargando usuarios..." />
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div className="flex items-center space-x-3 mb-4 md:mb-0">
                        <Users className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
                            <p className="text-gray-600">Administra los usuarios del sistema</p>
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar usuarios por nombre o email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <Users className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
                                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <Shield className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Administradores</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {users.filter(u => u.role === 'admin').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center">
                            <UserCheck className="h-8 w-8 text-green-600" />
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Usuarios Activos</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {users.filter(u => u.is_active).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Lista de Usuarios ({filteredUsers.length})
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Usuario
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Rol
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha de Registro
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                                    <User className="h-5 w-5 text-gray-600" />
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.full_name}
                                                </div>
                                                <div className="text-sm text-gray-500 flex items-center">
                                                    <Mail className="h-4 w-4 mr-1" />
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                                            {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                                            {getRoleText(user.role)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {user.is_active ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <UserCheck className="w-3 h-3 mr-1" />
                                                Activo
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                <UserX className="w-3 h-3 mr-1" />
                                                Inactivo
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 flex items-center">
                                            <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleView(user)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Ver detalles"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="text-green-600 hover:text-green-900"
                                                title="Editar"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(user)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        {filteredUsers.length === 0 && (
                            <div className="text-center py-8">
                                <Users className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {searchTerm ? 'No se encontraron usuarios con esa búsqueda' : 'No hay usuarios registrados'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal para Ver/Editar Usuario */}
                {showModal && selectedUser && (
                    <Modal
                        title={
                            modalType === 'edit' ? 'Editar Usuario' : 'Detalles del Usuario'
                        }
                        onClose={() => setShowModal(false)}
                        size="md"
                    >
                        {modalType === 'view' ? (
                            <UserDetails user={selectedUser} />
                        ) : (
                            <UserEditForm
                                user={selectedUser}
                                onSubmit={handleUpdateUser}
                                onClose={() => setShowModal(false)}
                            />
                        )}
                    </Modal>
                )}
            </div>
        </Layout>
    );
};

// Componente para mostrar detalles del usuario
const UserDetails = ({ user }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-gray-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">{user.full_name}</h3>
                    <p className="text-gray-600">{user.email}</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                        {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                        {getRoleText(user.role)}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Estado</h4>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                        {user.is_active ? <UserCheck className="w-3 h-3 mr-1" /> : <UserX className="w-3 h-3 mr-1" />}
                        {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Fecha de Registro</h4>
                    <p className="text-gray-900">{formatDate(user.created_at)}</p>
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Última Actualización</h4>
                    <p className="text-gray-900">{formatDate(user.updated_at)}</p>
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Superusuario</h4>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_superuser ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                        {user.is_superuser ? 'Sí' : 'No'}
                    </span>
                </div>
            </div>

            {/* Firma */}
            <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Firma</h4>
                {user.firma_imagen ? (
                    <div className="border border-gray-200 rounded-lg p-3 inline-block bg-white">
                        <img
                            src={user.firma_imagen}
                            alt="Firma del usuario"
                            className="max-h-24 max-w-xs object-contain"
                        />
                    </div>
                ) : (
                    <p className="text-gray-400 text-sm italic">Sin firma registrada</p>
                )}
            </div>
        </div>
    );
};

// Componente para editar usuario
const UserEditForm = ({ user, onSubmit, onClose }) => {
    const [formData, setFormData] = useState({
        full_name: user.full_name,
        role: user.role,
        is_active: user.is_active
    });
    const [firmaFile, setFirmaFile] = useState(null);
    const [firmaPreview, setFirmaPreview] = useState(user.firma_imagen || null);
    const fileInputRef = useRef(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({ ...formData, firmaFile });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFirmaChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFirmaFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setFirmaPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveFirma = () => {
        setFirmaFile(null);
        setFirmaPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo
                </label>
                <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol
                </label>
                <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="user">Usuario</option>
                    <option value="lector">Lector</option>
                    <option value="master">Master</option>
                    <option value="sst">SST</option>
                    <option value="validador">Validador</option>
                    <option value="limpieza">Limpieza</option>
                    <option value="rrhh">RRHH</option>
                    <option value="admin">Administrador</option>
                    <option value="xd">XD</option>
                </select>
            </div>

            <div className="flex items-center">
                <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                    Usuario activo
                </label>
            </div>

            {/* Firma */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Firma del Responsable
                </label>
                {firmaPreview ? (
                    <div className="relative inline-block border border-gray-200 rounded-lg p-3 bg-white">
                        <img
                            src={firmaPreview}
                            alt="Vista previa de firma"
                            className="max-h-24 max-w-xs object-contain"
                        />
                        <button
                            type="button"
                            onClick={handleRemoveFirma}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                            title="Quitar firma"
                        >
                            <XIcon className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                        <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Haz clic para subir la imagen de firma</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG hasta 2 MB</p>
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFirmaChange}
                    className="hidden"
                />
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit">
                    Actualizar Usuario
                </Button>
            </div>
        </form>
    );
};

export default UsersPage;